// ── 큰 카드 — KPI / 통계 위주 (2026-06-07) ────────────────────────────────
// SbContent 분리. M 사이즈 카운트 + KPI 4종.

import React from 'react';
import { Calendar, Phone, CreditCard, ClipboardList, Building, Users, Activity, Clock, CalendarCheck } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { isTomorrow, toMd } from '../utils';
import { UnifiedCard } from '../UnifiedCard';
import { accentText, accentBorder } from '../widgetAccent';
import { renderKpi, StatColumns, NamePills } from './parts';

// '내일 방문' 칸 (2026-10-25): 회사들의 일일콜 분류(서버 visit_categories — 상품 관리 > 회사에 붙이기). 없으면 그날 방문 분류(많은 순).
//   예전엔 '제주'·'영화' 두 칸이 코드에 고정이라 분류 이름이 다른 테넌트는 방문 목록이 늘 비었다. 첫 두 칸 색은 종전 그대로.
const VISIT_TONES = [
  'text-emerald-700 dark:text-emerald-300', 'text-rose-700 dark:text-rose-300', 'text-indigo-700 dark:text-indigo-300',
  'text-amber-700 dark:text-amber-300', 'text-cyan-700 dark:text-cyan-300', 'text-violet-700 dark:text-violet-300',
];
function visitColumns(configured, summary) {
  if (Array.isArray(configured) && configured.length) return configured;
  const sums = new Map();
  for (const it of summary || []) sums.set(it.category, (sums.get(it.category) || 0) + (Number(it.count) || 0));
  return [...sums.entries()].sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0]), 'ko-KR')).map(([c]) => c);
}

export function renderStatsCard(code, ctx) {
  const { stats, handlers, accentMap, navOnClickFor, visitCategories } = ctx;
  const ov = accentMap[code];
  // 카드 빈곳 클릭 → 연결 메뉴 navigate (사용자 명세 2026-06-18). 매핑 없으면 undefined.
  const onCard = navOnClickFor ? navOnClickFor(code) : undefined;
  const {
    onClickEmployees, onClickExternal, onClickSales, onClickOnline,
    onClickCheckedIn, onClickCheckedOut, onClickNotOut,
    onClickMonthCalls, onClickMonthContracts, onClickConversionRate, onClickPending,
    onClickCardRevenue,
  } = handlers;

  switch (code) {
    case 'kpi_employees':
      return renderKpi({ code, label: '재직 직원', value: stats.inServiceEmployees, accent: 'blue', accentOverride: ov, onClick: onClickEmployees, onCardClick: onCard, sortableId: code, watermarkIcon: Building });
    case 'kpi_external_users':
      return renderKpi({ code, label: '외부직원', value: stats.inServiceExternal, accent: 'violet', accentOverride: ov, onClick: onClickExternal, onCardClick: onCard, sortableId: code, watermarkIcon: Users });
    case 'kpi_sales_roster':
      return renderKpi({ code, label: '마케팅 명부', value: stats.inServiceSales, accent: 'sky', accentOverride: ov, onClick: onClickSales, onCardClick: onCard, sortableId: code, watermarkIcon: Users });
    case 'kpi_online_users':
      return renderKpi({ code, label: '현재 접속', value: stats.onlineCount, accent: 'emerald', accentOverride: ov, onClick: onClickOnline, onCardClick: onCard, sortableId: code, pulse: true, watermarkIcon: Activity });

    case 'attendance_today':
      return (
        <UnifiedCard key={code} code={code} title="출퇴근 통계" icon={Calendar} accent="emerald" accentOverride={ov} size="M" sortableId={code} watermarkIcon={Clock} onClick={onCard}>
          <StatColumns emphasis="lg" items={[
            { label: '출근', value: stats.checkedIn, color: '#059669', onClick: onClickCheckedIn },
            { label: '퇴근', value: stats.checkedOut, color: 'var(--text-sub)', onClick: onClickCheckedOut },
            { label: '미퇴근', value: stats.notCheckedOut, color: stats.notCheckedOut > 0 ? '#dc2626' : 'var(--text-sub)', danger: stats.notCheckedOut > 0, onClick: onClickNotOut },
          ]} />
          {stats.checkedInNames.length > 0 && (
            <div className="mt-1 pt-1 border-t border-[var(--border)]">
              <div className="text-[10px] font-bold text-[var(--text-sub)] mb-0.5 uppercase tracking-wider">출근 중</div>
              <NamePills names={stats.checkedInNames} />
            </div>
          )}
        </UnifiedCard>
      );

    case 'month_call_contract':
      return (
        <UnifiedCard key={code} code={code} title="이번달 콜·계약" icon={Phone} accent="indigo" accentOverride={ov} size="M" sortableId={code} watermarkIcon={Phone} onClick={onCard}>
          <StatColumns emphasis="lg" items={[
            { label: '콜 수', value: stats.monthCalls, color: '#6366f1', onClick: onClickMonthCalls },
            { label: '계약', value: stats.monthContracts, color: '#059669', onClick: onClickMonthContracts },
            { label: '계약율', value: `${stats.conversionRate}%`, color: '#d97706', onClick: onClickConversionRate },
          ]} />
          {stats.pendingContracts > 0 && (
            <div className="mt-2 pt-2 border-t border-[var(--border)]">
              <button
                onClick={(e) => { e.stopPropagation(); onClickPending(e); }}
                onPointerDown={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 bg-amber-50 border border-amber-300 rounded-full px-3 py-1 text-xs font-bold text-amber-800 hover:bg-amber-100 transition-colors dark:bg-amber-900/30 dark:text-amber-300"
              >
                {stats.pendingContracts}건 검토 대기
              </button>
            </div>
          )}
        </UnifiedCard>
      );

    case 'leave_status':
      return (
        <UnifiedCard key={code} code={code} title="연차 현황" icon={ClipboardList} accent="orange" accentOverride={ov} size="M" sortableId={code} watermarkIcon={CalendarCheck} onClick={onCard}>
          <div className="grid grid-cols-2 gap-3">
            {[{ title: '오늘', names: stats.todayLeaveNames }, { title: '예정', names: stats.upcomingLeaveEntries }].map(({ title, names }) => (
              <div key={title} className="flex flex-col gap-1">
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-black tabular-nums leading-none text-orange-700 dark:text-orange-300">{names.length}</span>
                  <span className="text-[10px] font-bold text-orange-600 dark:text-orange-400 uppercase tracking-wider">{title}</span>
                </div>
                {names.length === 0 ? (
                  <div className="text-[11px] text-[var(--text-muted)]">없음</div>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {names.map((n) => (
                      <span key={n} className="bg-orange-100 dark:bg-orange-900/40 border border-orange-300 dark:border-orange-600 rounded-full px-2 py-0.5 text-[10px] font-bold text-orange-800 dark:text-orange-300">{n}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </UnifiedCard>
      );

    case 'today_card_revenue':
      if (!stats.cardRevenue) return (
        <UnifiedCard key={code} code={code} title="오늘 카드결제" icon={CreditCard} accent="emerald" accentOverride={ov} size="M" sortableId={code} watermarkIcon={CreditCard} onClick={onCard}>
          <p className="text-xs text-[var(--text-muted)] py-2">데이터 없음</p>
        </UnifiedCard>
      );
      return (
        <UnifiedCard key={code} code={code} title="오늘 카드결제" icon={CreditCard} accent="emerald" accentOverride={ov} size="M" sortableId={code} watermarkIcon={CreditCard} onClick={onCard}>
          <StatColumns items={[
            { label: '총 건수', value: stats.cardRevenue.total_count.toLocaleString(), color: '#059669', onClick: () => onClickCardRevenue(null) },
            { label: '전화승인', value: stats.cardRevenue.phone_count.toLocaleString(), color: '#1d4ed8', onClick: () => onClickCardRevenue('전화승인') },
            { label: '카드승인', value: stats.cardRevenue.card_count.toLocaleString(), color: '#7c3aed', onClick: () => onClickCardRevenue('카드승인') },
          ]} />
          <div className="mt-2 pt-2 border-t border-[var(--border)] text-right">
            <div className="text-[10px] font-bold text-[var(--text-sub)] uppercase tracking-wider">총 금액</div>
            <div className="text-2xl font-black tabular-nums leading-none text-emerald-700 dark:text-emerald-300 mt-0.5">
              {stats.cardRevenue.total_amount.toLocaleString()}<span className="text-xs ml-0.5">원</span>
            </div>
          </div>
        </UnifiedCard>
      );

    case 'next_business_meetings': {
      // 2026-06-07: M → L 사이즈 + 본문 2열 grid (한 줄에 방문 데이터 2개) 통일 디자인.
      const tone = ov || 'indigo';
      return (
        <UnifiedCard
          key={code}
          code={code}
          title={`${isTomorrow(stats.tomorrow) ? '내일' : '다음 영업일'} 방문`}
          icon={Calendar}
          accent="indigo"
          accentOverride={ov}
          size="L"
          sortableId={code}
          sub={toMd(stats.tomorrow)}
          watermarkIcon={Calendar}
          onClick={onCard}
        >
          {stats.meetingSummary.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-4">
              <span className="text-3xl font-black text-[var(--text-muted)] leading-none">0</span>
              <span className="text-[11px] text-[var(--text-muted)] mt-1">예정된 방문 없음</span>
            </div>
          ) : (
            <>
              {/* 헤더 핵심값: tomorrowMeetingCount + "건" (text-xl accent) */}
              <div className="flex items-baseline justify-end gap-1 mb-2">
                <span className={cn('text-xl font-black tabular-nums leading-none', accentText(tone))}>
                  {stats.tomorrowMeetingCount}
                </span>
                <span className="text-[10px] font-bold text-[var(--text-sub)] uppercase tracking-wider">건</span>
              </div>
              {/* 구분선 */}
              <div className={cn('border-t mx-1 my-1.5', accentBorder(tone))} />
              {/* 본문: 2열 grid — 칸 = 회사들의 일일콜 분류(visitColumns, 설정에 없는 분류는 표시 X).
                  카테고리명 옆 숫자 = 각 라인 count 합 (실제 미팅 건수). */}
              {(() => {
                const columns = visitColumns(visitCategories, stats.meetingSummary);
                const sumCount = (items) => items.reduce((s, it) => s + (Number(it.count) || 0), 0);
                const renderLine = (item, keySuffix) => (
                  <div
                    key={`${item.sender}__${item.marketer}__${item.region}__${keySuffix}`}
                    className="flex items-baseline gap-1 text-[9px] text-[var(--text-muted)] min-w-0"
                  >
                    <span className="truncate flex-shrink-0">{item.sender}</span>
                    <span className="flex-shrink-0">→</span>
                    <span className="font-bold text-[var(--text-sub)] truncate flex-shrink-0">{item.marketer}</span>
                    <span className="truncate flex-1">{item.region}</span>
                    <span className="font-black text-indigo-500 whitespace-nowrap flex-shrink-0">{item.count}건</span>
                  </div>
                );
                return (
                  <div className="grid grid-cols-2 gap-x-2 gap-y-2 w-full">
                    {columns.map((cat, i) => {
                      const items = stats.meetingSummary.filter((it) => it.category === cat);
                      return (
                        <div key={cat} className="flex flex-col gap-0.5 min-w-0">
                          <div className={cn('text-[10px] font-bold mb-0.5', VISIT_TONES[i % VISIT_TONES.length])}>{cat} ({sumCount(items)}건)</div>
                          {items.map((it) => renderLine(it, i))}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </>
          )}
        </UnifiedCard>
      );
    }

    default:
      return null;
  }
}
